import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from '../../components/Icon.tsx';
import { orgSettingsApi, type Succession, type SuccessionRow } from './api.ts';
import { dv } from '../../i18n/dv.ts';
import type { Body } from '../../lib/body.ts';
import type { Role } from '../../store/auth.ts';

// Amber pill-switch for a member's "on leave" state (mirrors the Profile toggle).
function LeaveSwitch({ on, disabled, onToggle }: { on: boolean; disabled: boolean; onToggle: () => void }): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={dv.settings.chair.onLeave}
      disabled={disabled}
      onClick={onToggle}
      dir="ltr"
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${on ? 'bg-amber-500' : 'bg-zinc-300 dark:bg-zinc-600'}`}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${on ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
    </button>
  );
}

export function ChairDelegationCard({ body }: { body: Body }): JSX.Element {
  const qc = useQueryClient();
  const key = ['succession', body];
  const q = useQuery({ queryKey: key, queryFn: () => orgSettingsApi.succession(body) });

  const leaveMut = useMutation({
    mutationFn: ({ userId, onLeave }: { userId: string; onLeave: boolean }) => orgSettingsApi.setChairLeave(body, userId, onLeave),
    onSuccess: (data: Succession) => qc.setQueryData(key, data),
  });
  const orderMut = useMutation({
    mutationFn: (memberIds: string[]) => orgSettingsApi.setChairOrder(body, memberIds),
    onSuccess: (data: Succession) => qc.setQueryData(key, data),
  });

  const rows: SuccessionRow[] = q.data?.rows ?? [];
  const members = rows.filter((r) => !r.isHead);
  const busy = leaveMut.isPending || orderMut.isPending;

  const move = (memberIdx: number, dir: -1 | 1): void => {
    const ids = members.map((m) => m.id);
    const j = memberIdx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[memberIdx], ids[j]] = [ids[j]!, ids[memberIdx]!];
    orderMut.mutate(ids);
  };

  return (
    <section className="space-y-3 rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
      <h2 className="status-label text-muted-foreground">{dv.settings.chair.title}</h2>
      <p className="text-xs leading-relaxed text-muted-foreground">{dv.settings.chair.help}</p>

      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">{dv.common.loading}</p>
      ) : (
        <>
          <ul className="divide-y divide-black/[0.05]">
            {rows.map((r, i) => {
              const memberIdx = r.isHead ? -1 : members.findIndex((m) => m.id === r.id);
              return (
                <li key={r.id} className="flex items-center gap-3 py-2.5">
                  <span className="w-4 shrink-0 tabular text-xs text-muted-foreground" dir="ltr">{i + 1}</span>
                  <span className="min-w-0 flex-1">
                    <span className="font-medium">{r.nameDv || r.name}</span>
                    {r.isActingChair && (
                      <span className="pill pill-active ms-2 text-[0.7rem]">{dv.settings.chair.actingBadge}</span>
                    )}
                    <span className="ms-2 block text-xs text-muted-foreground sm:inline">
                      {dv.roles[r.role as Role]}
                    </span>
                  </span>

                  {/* Reorder — members only (president/VP are fixed at the top). */}
                  {!r.isHead && (
                    <span className="flex shrink-0 flex-col">
                      <button
                        type="button" aria-label={dv.settings.chair.moveUp}
                        disabled={busy || memberIdx === 0}
                        onClick={() => move(memberIdx, -1)}
                        className="text-muted-foreground hover:text-ink disabled:opacity-30"
                      >
                        <Icon name="keyboard_arrow_up" size={18} />
                      </button>
                      <button
                        type="button" aria-label={dv.settings.chair.moveDown}
                        disabled={busy || memberIdx === members.length - 1}
                        onClick={() => move(memberIdx, 1)}
                        className="text-muted-foreground hover:text-ink disabled:opacity-30"
                      >
                        <Icon name="keyboard_arrow_down" size={18} />
                      </button>
                    </span>
                  )}

                  <span className="flex shrink-0 items-center gap-2">
                    <span className={`text-xs ${r.onLeave ? 'text-amber-600' : 'text-muted-foreground'}`}>{dv.settings.chair.onLeave}</span>
                    <LeaveSwitch on={r.onLeave} disabled={busy} onToggle={() => leaveMut.mutate({ userId: r.id, onLeave: !r.onLeave })} />
                  </span>
                </li>
              );
            })}
          </ul>

          {q.data && !q.data.actingChairId && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">{dv.settings.chair.noneActing}</p>
          )}
        </>
      )}
    </section>
  );
}
